import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep } from '../../atoms';
import { useAtom } from 'jotai';
import { stepConfigAtom } from '../../atoms';
import DocumentationRequirementCard from '../../__components/DocumentationRequirementCard';
import { combineDocumentationRequirements, FAMILY_FILE_TYPES } from 'services/family.service';
import { CardTitle } from 'components/Card/Card';
import PageMeta from 'components/PageMeta/PageMeta';
import { ChildCysRevealingLink } from './__components/ChildCysRevealingLink';
import { getFirstName } from 'utils';
import { CheckmarkFilled } from '@carbon/icons-react';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import FamilyAppBlankFormDownloadButton from '../../__components/FamilyAppBlankFormDownloadButton';

export function FamilyApplicationChildrenDocuments({ application, setStep, loading, readOnly = false }) {
  const [stepConfig] = useAtom(stepConfigAtom);

  // Load application files
  const [files, setFiles] = useState(null);
  const [documentationRequirements, setDocumentationRequirements] = useState(null);

  useEffect(() => {
    if (application?.id) {
      familyService.getApplicationFiles(application.id).then((applicationFiles) => {
        setFiles(applicationFiles || []);
      });
    }
  }, [application?.id]);

  // Load children documentation requirements
  useEffect(() => {
    if (application?.id && application?.children?.[0]?.id) {
      familyService
        .getChildDocumentationRequirements(
          application.id,
          application.children?.map((child) => child.id),
        )
        .then((requirements) => {
          const _combinedRequirements = combineDocumentationRequirements(requirements);
          setDocumentationRequirements(_combinedRequirements);
        })
        .catch((error) => {
          console.error('Failed to load spouse documentation requirements:', error);
          setDocumentationRequirements([]);
        });
    }
  }, [application?.children, application?.id]);

  if (loading || files === null) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Children's Documents" />

      <div className="p-8">
        <Banner
          question={
            documentationRequirements?.length === 0
              ? 'No documents needed for your child(ren)'
              : application?.children?.length > 1
              ? 'Documents for your children'
              : `Documents for ${getFirstName(application?.children?.[0])}`
          }
          guidance={
            <>
              {documentationRequirements?.length === 0 && (
                <CheckmarkFilled className="w-5 h-5 mb-4 inline-block align-middle mb-[3px] mr-2" />
              )}

              {documentationRequirements?.length === 0
                ? "Based on your answers, we don't need any documents for your children at this time."
                : "Based on your answers, here's what you'll need"}
            </>
          }
          className="mb-12"
        />

        {/* Dynamic Documentation Cards */}
        {documentationRequirements?.map((requirement, index) => {
          return (
            <DocumentationRequirementCard
              key={requirement.fileType}
              requirement={requirement}
              title={<CardTitle title={requirement.title} icon="📋" />}
              showRequirementStatus
              showPersonName
              descriptionsFooter={
                requirement.fileType === FAMILY_FILE_TYPES.CHILD_CYS?.code ? <ChildCysRevealingLink /> : null
              }
              uploadFileProps={
                [FAMILY_FILE_TYPES.PROVIDER_RATES?.code, FAMILY_FILE_TYPES.CHILD_CYS?.code].includes(
                  requirement.fileType,
                )
                  ? {
                      uploadButtonText: 'Upload form(s)',
                      preUploadButtonContent: (
                        <FamilyAppBlankFormDownloadButton
                          fileType={requirement.fileType}
                          branchId={application?.applicant?.militaryDetails?.branch?.id}
                        />
                      ),
                    }
                  : {}
              }
              files={files}
              setFiles={setFiles}
              application={application}
              readOnly={readOnly}
              className={index > 0 ? 'mt-10' : ''}
            />
          );
        })}
      </div>

      <ApplicationWizardActions
        applicationIsReadOnly={readOnly}
        backButtonProps={{
          onClick: () => {
            const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS, application, stepConfig);
            setStep({
              applicationId: application?.id,
              newStep: previousStep,
            });
          },
          'data-testid': 'children-documents-back-button',
        }}
        nextButtonProps={{
          text: 'Next',
          onClick: () => {
            const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.CHILDREN_DOCUMENTS, application, stepConfig);
            setStep({
              newStep: nextStep,
              applicationId: application?.id,
            });
          },
          'data-testid': 'children-documents-next-button',
        }}
      />
    </>
  );
}
